import type { PlainMessage } from '@bufbuild/protobuf';
import cn from 'classnames';
import { omit } from 'lodash-es';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type {
  NoteTeethBlock,
  NoteTeethGroup,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import type { NoteToothNotation } from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';

import { noteTeethOfferedGroups } from '@/entities/templates';

import { i18n } from '../config';
import {
  addNoteGroupMark,
  cycleNoteToothCode,
  formatNoteDecimal,
  formatNoteTooth,
  listNoteToothCodes,
  listNoteToothZones,
  parseNoteDecimal,
  printNoteNone,
  printNoteUnit,
  readNoteSurfaceCode,
  readNoteTeethMarksDentition,
  readNoteTeethMarksPresentation,
  readNoteToothCodes,
  readNoteToothNumber,
  setNoteSurfaceCode,
  setNoteToothCodes,
  setNoteToothNumber,
  showNoteToothCode,
  type NoteMark,
} from '../lib';

import styles from './NoteTeethMarks.module.scss';
import { TeethChart } from './NoteTeethPicker';

type Props = {
  block: PlainMessage<NoteTeethBlock>;
  marks: NoteMark[];
  none: boolean;
  // The teeth whose marks AI set.
  aiTeeth: string[];
  notation?: NoteToothNotation;
  // The template's language, which the field's words follow.
  language?: string;
  onChange: (marks: NoteMark[]) => void;
  onNone: (none: boolean) => void;
};

// The palette's colours, in the order of the field's codes.
const CODE_TONES = ['purple', 'orange', 'green', 'red', 'blue', 'peach'];

// What one tooth or group the clinician opened takes: its codes.
type Picking = { tooth: string } | { group: NoteTeethGroup };

// A teeth field that stores marks, filled the way its settings say: codes
// picked on a tooth, one code a click moves through, a code per surface
// from a palette, or a number per tooth. Every change is the field's whole
// set of marks, sent at once as one step of the note's history.
export const NoteTeethMarks: FC<Props> = ({
  block,
  marks,
  none,
  aiTeeth,
  notation,
  language,
  onChange,
  onNone,
}) => {
  const { formatMessage } = useIntl();
  const presentation = readNoteTeethMarksPresentation(block);
  const [picking, setPicking] = useState<Picking | null>(null);
  // The palette's code a surface takes; null is the eraser.
  const [active, setActive] = useState<string | null>(
    block.Codes[0]?.Code ?? null,
  );
  const [typed, setTyped] = useState<Record<string, string>>({});
  const codeTone = (code: string) =>
    CODE_TONES[
      Math.max(
        block.Codes.findIndex((item) => item.Code === code),
        0,
      ) % CODE_TONES.length
    ];
  const toothLabel = (tooth: string) =>
    formatMessage(
      { id: 'workspace.note.teeth.tooth', defaultMessage: 'Tooth {tooth}' },
      { tooth: formatNoteTooth(tooth, notation) },
    );

  const change = (next: NoteMark[]) => {
    if (none) {
      onNone(false);
    }
    onChange(next);
  };

  const commitNumber = (tooth: string) => {
    const text = typed[tooth];
    if (text === undefined) {
      return;
    }
    const number = text.trim()
      ? parseNoteDecimal(text, block.Precision ?? 0)
      : null;
    if (text.trim() && number === null) {
      return;
    }
    setTyped((current) => omit(current, tooth));
    if (number !== readNoteToothNumber(marks, tooth)) {
      change(setNoteToothNumber(marks, tooth, number));
    }
  };

  const renderSurfaces = (tooth: string) => {
    const zones = listNoteToothZones(tooth);
    const zone = (
      surface: string,
      points: string,
      className: string = styles.zone,
    ) => {
      const code = readNoteSurfaceCode(marks, tooth, surface);
      return (
        <polygon
          key={surface}
          points={points}
          className={cn(className, code && styles[codeTone(code)])}
          data-surface={surface}
          data-code={code}
          onClick={() =>
            change(setNoteSurfaceCode(marks, tooth, surface, active))
          }
        />
      );
    };
    return (
      <svg
        viewBox="0 0 30 30"
        className={styles.surfaces}
        role="group"
        aria-label={toothLabel(tooth)}
      >
        {zone(zones.top, '0,0 30,0 21,9 9,9')}
        {zone(zones.right, '30,0 30,30 21,21 21,9')}
        {zone(zones.bottom, '30,30 0,30 9,21 21,21')}
        {zone(zones.left, '0,30 0,0 9,9 9,21')}
        {zone(zones.centre, '9,9 21,9 21,21 9,21')}
      </svg>
    );
  };

  const renderTooth = (tooth: string, lower: boolean) => {
    const codes = listNoteToothCodes(block, tooth);
    const label = formatNoteTooth(tooth, notation);
    const ai = aiTeeth.includes(tooth);
    const number = (
      <span className={cn('p4', styles.number, ai && styles.ai)}>{label}</span>
    );
    if (presentation === 'surfaces') {
      return (
        <span
          key={tooth}
          className={cn(styles.tooth, lower && styles.lower)}
          data-tooth={tooth}
        >
          {number}
          {renderSurfaces(tooth)}
        </span>
      );
    }
    if (presentation === 'numbers') {
      const stored = readNoteToothNumber(marks, tooth);
      const value = typed[tooth] ?? formatNoteDecimal(stored ?? '', language);
      const invalid =
        !!value.trim() &&
        parseNoteDecimal(value, block.Precision ?? 0) === null;
      return (
        <span
          key={tooth}
          className={cn(styles.tooth, lower && styles.lower)}
          data-tooth={tooth}
        >
          {number}
          <input
            className={cn('p4', styles.input, invalid && styles.invalid)}
            inputMode="decimal"
            aria-label={toothLabel(tooth)}
            aria-invalid={invalid}
            value={value}
            onChange={(event) =>
              setTyped((current) => ({
                ...current,
                [tooth]: event.target.value,
              }))
            }
            onBlur={() => commitNumber(tooth)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitNumber(tooth);
              }
            }}
          />
        </span>
      );
    }
    const held = readNoteToothCodes(marks, tooth);
    return (
      <button
        key={tooth}
        type="button"
        className={cn(
          styles.tooth,
          styles.toothButton,
          lower && styles.lower,
          held.length > 0 && styles.marked,
          ai && styles.ai,
        )}
        aria-label={toothLabel(tooth)}
        title={
          held
            .map((code) => block.Codes.find((item) => item.Code === code))
            .map((code) => code?.Label ?? '')
            .join(', ') || undefined
        }
        aria-pressed={held.length > 0}
        aria-expanded={
          presentation === 'codes'
            ? picking !== null && 'tooth' in picking && picking.tooth === tooth
            : undefined
        }
        disabled={!codes.length}
        data-tooth={tooth}
        onClick={() =>
          presentation === 'cycle'
            ? change(cycleNoteToothCode(block, marks, tooth))
            : setPicking({ tooth })
        }
      >
        {number}
        <span className={cn('p4', styles.codes)}>
          {held
            .map((code) => block.Codes.find((item) => item.Code === code))
            .map((code) => (code ? showNoteToothCode(code) : ''))
            .join(' ')}
        </span>
      </button>
    );
  };

  const renderPicking = () => {
    if (!picking) {
      return null;
    }
    const tooth = 'tooth' in picking ? picking.tooth : null;
    const held = tooth ? readNoteToothCodes(marks, tooth) : [];
    const codes = tooth ? listNoteToothCodes(block, tooth) : block.Codes;
    return (
      <div
        className={styles.picking}
        role="group"
        aria-label={
          tooth
            ? toothLabel(tooth)
            : formatMessage(
                i18n.noteTeethGroupMessages[
                  (picking as { group: NoteTeethGroup }).group
                ],
              )
        }
        data-component="workspace-note-teeth-codes"
      >
        {codes.map((code) => {
          const chosen = held.includes(code.Code);
          return (
            <button
              key={code.Code}
              type="button"
              className={cn('p3', styles.code)}
              aria-pressed={chosen}
              onClick={() => {
                if (!tooth) {
                  change(
                    addNoteGroupMark(
                      marks,
                      (picking as { group: NoteTeethGroup }).group,
                      [code.Code],
                    ),
                  );
                  setPicking(null);
                  return;
                }
                change(
                  setNoteToothCodes(
                    marks,
                    tooth,
                    chosen
                      ? held.filter((item) => item !== code.Code)
                      : [...held, code.Code],
                  ),
                );
              }}
            >
              {code.Label}
            </button>
          );
        })}
        <button
          type="button"
          className={cn('p3', styles.done)}
          onClick={() => setPicking(null)}
        >
          <FormattedMessage
            id="workspace.note.collapse"
            defaultMessage="Done"
          />
        </button>
      </div>
    );
  };

  const groupsOffered =
    presentation === 'codes' || presentation === 'cycle'
      ? noteTeethOfferedGroups(block)
      : [];
  const noneLabel = printNoteNone(block, language);

  return (
    <div
      className={styles.marks}
      data-component="workspace-note-teeth-marks"
      data-presentation={presentation}
    >
      {(presentation === 'surfaces' || block.AllowNone) && (
        <div className={styles.palette}>
          {presentation === 'surfaces' && (
            <>
              {block.Codes.map((code) => (
                <button
                  key={code.Code}
                  type="button"
                  className={cn('p3', styles.code)}
                  aria-pressed={active === code.Code}
                  onClick={() => setActive(code.Code)}
                >
                  <span
                    className={cn(styles.swatch, styles[codeTone(code.Code)])}
                    aria-hidden
                  />
                  {code.Label}
                </button>
              ))}
              <button
                type="button"
                className={cn('p3', styles.code)}
                aria-pressed={active === null}
                onClick={() => setActive(null)}
              >
                <FormattedMessage
                  id="workspace.note.teeth.eraser"
                  defaultMessage="Eraser"
                />
              </button>
            </>
          )}
          {block.AllowNone && (
            <button
              type="button"
              className={cn('p3', styles.code, styles.none)}
              aria-pressed={none}
              data-component="workspace-note-none"
              onClick={() => onNone(!none)}
            >
              {noneLabel}
            </button>
          )}
        </div>
      )}
      <TeethChart
        dentition={readNoteTeethMarksDentition(block)}
        offeredGroups={groupsOffered}
        isGroupPressed={() => false}
        renderTooth={renderTooth}
        onGroup={(group) => setPicking({ group })}
      />
      {renderPicking()}
      {presentation === 'numbers' && block.Unit && (
        <p className={cn('p4', styles.hint)}>
          <FormattedMessage
            id="workspace.note.teeth.unit"
            defaultMessage="In {unit}"
            values={{ unit: printNoteUnit(block.Unit, language) }}
          />
        </p>
      )}
    </div>
  );
};
